import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass,
  CaretDown,
  DotsThreeVertical,
  Vault,
  Microphone,
  User as UserIcon,
  Target,
  ArrowSquareOut,
  Trash,
  PencilSimple,
  FileText,
  FilePdf,
  Newspaper,
  EnvelopeSimple,
  DownloadSimple,
  MonitorPlay,
  Layout,
  ChatsCircle,
} from "@phosphor-icons/react";
import {
  brandVoiceBlueprintToMarkdown,
  webinarBlueprintToMarkdown,
  downloadMarkdown,
} from "../lib/blueprintToMarkdown";
import { toast } from "sonner";
import { api } from "../lib/api";
import { runPdfExportJob, downloadBlobAsFile } from "../lib/pdfExportJob";
import BlueprintPreviewModal from "../components/BlueprintPreviewModal";
import MagnetPostPreviewModal from "../components/MagnetPostPreviewModal";
import { AGENT_LABELS } from "../components/blueprintRenderers";

// User-facing type metadata - mirrors the dashboard layer color scheme:
//   - Alapozás (lila #5e50f7): Márkahang, Avatar, Ajánlat
//   - Vonzás   (ciánkék #75dff5): Poszt-*, Email-*, Mágnes
//   - Értékesítés (arany #ffdb5c): Webinár, Landing, Sales
// NOTE: backend `agent_type` keys (voice/client/offer/post-*/lead-magnet/...)
// are internal IDs and stay unchanged.
const FOUNDATION_COLOR = "#5e50f7";
const ATTRACTION_COLOR = "#75dff5";
const CONVERSION_COLOR = "#ffdb5c";

const WEBINAR_TYPES = new Set([
  "webinar",
  "webinar-callbook-funnel",
  "webinar-callback-funnel",
  "webinar-direct-sales",
  "webinar-vazlat",
  // Landing regisztrációs oldal is webinár-shape mentett tartalom, ezért
  // a Tudástár lista + preview + MD export ugyanazt a flow-t használja.
  "landing-webinar-registration",
]);

const AGENT_OPTIONS = [
  { value: "all", label: "Minden típus" },
  { value: "voice",  label: "Márkahang", accent: FOUNDATION_COLOR, icon: Microphone },
  { value: "client", label: "Avatar",    accent: FOUNDATION_COLOR, icon: UserIcon },
  { value: "offer",  label: "Ajánlat",   accent: FOUNDATION_COLOR, icon: Target },
  { value: "webinar-callbook-funnel", label: "Webinár – telefonhívás",       accent: CONVERSION_COLOR },
  { value: "webinar-direct-sales",    label: "Webinár – direkt értékesítés", accent: CONVERSION_COLOR },
  { value: "landing-webinar-registration", label: "Webinár regisztrációs oldal", accent: CONVERSION_COLOR },
  { value: "post-facebook", label: "Facebook poszt", accent: ATTRACTION_COLOR },
];

// User-facing magyar címkék a backend `agent_type` slug-okhoz. A slug az
// adatbázisban változatlanul marad — csak a felületen jelenítünk meg
// szép magyar címkét, badge-et és filter dropdown-elemet.
const AGENT_META = {
  voice:           { label: "Márkahang",          accent: FOUNDATION_COLOR, icon: Microphone,    secondary: "Márkahang (Brand Voice)" },
  client:          { label: "Avatar",             accent: FOUNDATION_COLOR, icon: UserIcon,      secondary: "Ideális ügyfélprofil (Avatar)" },
  offer:           { label: "Ajánlat",            accent: FOUNDATION_COLOR, icon: Target,        secondary: "Prémium ajánlat" },
  "post-facebook": { label: "Facebook poszt",     accent: ATTRACTION_COLOR, icon: Newspaper,     secondary: "Poszt - Facebook" },
  "post-linkedin": { label: "LinkedIn poszt",     accent: ATTRACTION_COLOR, icon: Newspaper,     secondary: "Poszt - LinkedIn" },
  "post-reels":    { label: "IG Reels script",    accent: ATTRACTION_COLOR, icon: Newspaper,     secondary: "Poszt - IG Reels" },
  "post-carousel": { label: "Carousel",           accent: ATTRACTION_COLOR, icon: Newspaper,     secondary: "Poszt - Carousel" },
  "email":         { label: "Email",              accent: ATTRACTION_COLOR, icon: EnvelopeSimple, secondary: "Email anyag" },
  "email-welcome": { label: "Üdvözlő sorozat",    accent: ATTRACTION_COLOR, icon: EnvelopeSimple, secondary: "Email - Üdvözlő sorozat" },
  "email-value":   { label: "Értékadó email",     accent: ATTRACTION_COLOR, icon: EnvelopeSimple, secondary: "Email - Értékadó" },
  "email-handraiser": { label: "Kézfelemelő email", accent: ATTRACTION_COLOR, icon: EnvelopeSimple, secondary: "Email - Kézfelemelő" },
  "email-webinar": { label: "Webinár promó",      accent: ATTRACTION_COLOR, icon: EnvelopeSimple, secondary: "Email - Webinár promó" },
  "lead-magnet":   { label: "Mikromágnes™",       accent: ATTRACTION_COLOR, icon: DownloadSimple, secondary: "Ügyfélmágnes - Mikromágnes" },
  // Webinár típusok — a backend a callbook_funnel / direct_sales agent
  // sub-variantok szerint ment (mindkettőt konverziós webinárként
  // tartjuk nyilván, arany akcenttel).
  "webinar":                 { label: "Webinár",                     accent: CONVERSION_COLOR, icon: MonitorPlay, secondary: "Webinár anyag" },
  "webinar-callbook-funnel": { label: "Webinár – telefonhívás",      accent: CONVERSION_COLOR, icon: MonitorPlay, secondary: "Webinár – Webinárból telefonhívás" },
  "webinar-callback-funnel": { label: "Webinár – telefonhívás",      accent: CONVERSION_COLOR, icon: MonitorPlay, secondary: "Webinár – Webinárból telefonhívás" },
  "webinar-direct-sales":    { label: "Webinár – direkt értékesítés", accent: CONVERSION_COLOR, icon: MonitorPlay, secondary: "Webinár – Közvetlen értékesítés" },
  "webinar-vazlat":          { label: "Webinár vázlat",              accent: CONVERSION_COLOR, icon: MonitorPlay, secondary: "Webinár stratégiai vázlat" },
  "landing-webinar-registration": { label: "Webinár regisztrációs oldal", accent: CONVERSION_COLOR, icon: Layout,     secondary: "Landing – Webinár regisztrációs oldal" },
  "landing":       { label: "Landing oldal",      accent: CONVERSION_COLOR, icon: Layout,        secondary: "Landing oldal" },
  "sales":         { label: "Sales script",       accent: CONVERSION_COLOR, icon: ChatsCircle,   secondary: "Sales beszélgetés" },
};

function getAgentMeta(agentType) {
  return AGENT_META[agentType] || { label: agentType || "Anyag", accent: FOUNDATION_COLOR, icon: Vault, secondary: "" };
}

export default function AssetVault() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/asset-vault");
        setItems(res.data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (agentFilter !== "all") list = list.filter((i) => i.agent_type === agentFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) || i.agent_label.toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, agentFilter, search]);

  const handleOpen = (item) => {
    // Ugyanaz a route-mapping mint a preview modalban: a webinár
    // sub-variantok mind a KÖZÖS `/agents/webinar` varázslóra mennek.
    // Webinároknál a mentett blueprint-et is átadjuk `state.savedItem`-en
    // — a WebinarAgent read-only viewer módban rendereli a Carousel-t
    // ÚJ generálás nélkül.
    const mapped = AGENT_LABELS?.[item.agent_type]?.route;
    const target = mapped || `/agents/${item.agent_type}`;
    if (WEBINAR_TYPES.has(item.agent_type)) {
      navigate(target, { state: { savedItem: item } });
    } else if (item.agent_type === "voice" || item.agent_type === "client" || item.agent_type === "offer") {
      // F3 varázslók: a mentett snapshotot a `?saved_id=` query-paraméteren
      // adjuk át. Az `AgentWorkflow` felismeri, GET-eli a `/asset-vault/{id}`
      // végpontról, majd az output nézetben rendereli ÚJ generálás nélkül.
      navigate(`${target}?saved_id=${encodeURIComponent(item.id)}`);
    } else {
      navigate(target);
    }
  };
  const handlePreview = (item) => setPreviewItem(item);

  const startRename = (item) => {
    setRenameValue(item.name || "");
    setRenamingId(item.id);
  };

  const submitRename = async (item) => {
    const next = renameValue.trim();
    // Nincs változás → csendben zárjuk. Üres név → hibaüzenet.
    if (!next) {
      toast.error("A név nem lehet üres");
      setRenamingId(null);
      return;
    }
    if (next === item.name) {
      setRenamingId(null);
      return;
    }
    if (next.length > 250) {
      toast.error("Max. 250 karakter");
      return; // maradjon szerkesztés módban, hogy a user rövidíthesse
    }
    try {
      await api.patch(`/asset-vault/${item.id}`, { name: next });
      // A backend a TELJES új nevet menti; a UI-ban a listát is a full
      // névvel frissítjük. A display csak vizuálisan rövidít (ellipszis).
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, name: next } : x)));
      // Ha épp preview van nyitva ugyanerre az elemre, frissítsük is,
      // hogy a modal fejléc + export az új nevet mutassa.
      setPreviewItem((prev) => (prev && prev.id === item.id ? { ...prev, name: next } : prev));
      toast.success("Átnevezve");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Átnevezés sikertelen";
      toast.error(msg);
    } finally {
      setRenamingId(null);
    }
  };

  const handleDownloadMd = async (item) => {
    try {
      const res = await api.get(`/asset-vault/${item.id}`);
      const bp = res.data?.blueprint;
      if (!bp) return;
      // Currently only Brand Voice has the structured 9-section MD serializer.
      // Avatar / Offer fall back to a JSON-as-code-block dump until their own
      // MD serializers land. Webinárok a `webinarBlueprintToMarkdown`-ot
      // használják, ami tartalmazza a szekciókat, slide-okat, headline-t,
      // bulletöket, CTA-t / kérdést, előadói jegyzetet és vizuális javaslatot.
      let md;
      if (item.agent_type === "voice") {
        md = brandVoiceBlueprintToMarkdown(bp, item.name);
      } else if (WEBINAR_TYPES.has(item.agent_type)) {
        md = webinarBlueprintToMarkdown(bp, item.name, res.data?.saved_at || item.saved_at);
      } else {
        md = `# ${item.name}\n\n\`\`\`json\n${JSON.stringify(bp, null, 2)}\n\`\`\`\n`;
      }
      downloadMarkdown(item.name, md);
    } catch {
      // swallow
    }
  };

  const handleDownloadPdf = async (item) => {
    // Foundation Avatar/Ajánlat/Márkahang → szerveroldali Playwright PDF
    // a TÉNYLEGES UI riportel. Single source of truth: ugyanaz a render-
    // függvény, mint az appban (renderAvatarBlueprintV3 / renderOfferBlueprintV3 /
    // renderVoiceBlueprintV2). Más típusok (post-* / email-* / lead-magnet-*)
    // saját letöltési flow-t használnak — itt nem kezeltek.
    if (item.agent_type === "client" || item.agent_type === "offer" || item.agent_type === "voice") {
      try {
        // Async job flow — start → poll → download.
        const blob = await runPdfExportJob({
          start: () =>
            api.post("/export/pdf/blueprint/start", {
              blueprint_type: item.agent_type,
              entry_id: item.id,
            }),
        });
        downloadBlobAsFile(blob, `${item.name || item.agent_type}.pdf`);
        toast.success("PDF letöltve");
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Ismeretlen hiba";
        toast.error("PDF generálás sikertelen: " + detail);
      }
      return;
    }
    toast.error("Ehhez a típushoz nincs PDF export");
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <div className="w-11 h-11 rounded-xl bg-[#1f1f2b] border border-white/[0.08] flex items-center justify-center">
          <Vault size={22} color="#5e50f7" weight="duotone" />
        </div>
        <div>
          <div className="mex-eyebrow mb-2">Stúdió · Tudástár</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-3">Tudástár</h1>
          <p className="text-[#c3c3cc] text-[14px] max-w-2xl leading-relaxed">
            Minden elmentett alapod és marketinganyagod egy helyen.
          </p>
        </div>
      </div>

      {/* Filter + search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative">
          <button
            onClick={() => setFilterOpen((o) => !o)}
            data-testid="asset-filter-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1f1f2b] border border-white/[0.08] text-[13px] text-white hover:bg-[#26263a] transition min-w-[220px] justify-between"
          >
            <span>{AGENT_OPTIONS.find((o) => o.value === agentFilter)?.label}</span>
            <CaretDown size={12} />
          </button>
          {filterOpen && (
            <div className="absolute top-full mt-1.5 left-0 z-30 min-w-[220px] rounded-xl bg-[#1f1f2b] border border-white/[0.1] shadow-2xl overflow-hidden">
              {AGENT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setAgentFilter(opt.value);
                    setFilterOpen(false);
                  }}
                  data-testid={`filter-opt-${opt.value}`}
                  className={`w-full px-4 py-2.5 text-left text-[13px] transition ${
                    agentFilter === opt.value
                      ? "bg-[#5e50f7]/15 text-white"
                      : "text-[#c3c3cc] hover:bg-white/[0.05]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={16}
            color="#a1a1a1"
            className="absolute left-3.5 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés a mentett anyagok között..."
            data-testid="asset-search"
            className="w-full bg-[#1f1f2b] border border-white/[0.08] rounded-xl pl-10 pr-4 py-2.5 text-[13px] text-white placeholder:text-[#6a6a78] outline-none focus:border-[#5e50f7]/70 focus:ring-1 focus:ring-[#5e50f7]/40"
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-2xl border border-white/[0.08]"
        style={{ background: "#17171f" }}
        data-testid="asset-table"
      >
        {/* head */}
        <div className="grid grid-cols-[1fr_200px_160px_60px] px-5 py-3 border-b border-white/[0.06] text-[10px] tracking-[0.2em] uppercase text-[#a1a1a1]">
          <div>Név</div>
          <div>Típus</div>
          <div>Dátum</div>
          <div className="text-right">Műveletek</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-[#a1a1a1] text-[13px]">Betöltés...</div>
        ) : filtered.length === 0 ? (
          <EmptyState search={search} total={items.length} />
        ) : (
          filtered.map((item) => {
            const meta = getAgentMeta(item.agent_type);
            const Icon = meta.icon;
            return (
              <div
                key={item.id}
                onClick={() => handlePreview(item)}
                data-testid={`asset-row-${item.id}`}
                className="group grid grid-cols-[1fr_200px_160px_60px] items-center px-5 py-4 border-b border-white/[0.04] hover:bg-white/[0.025] cursor-pointer transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {Icon && (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{
                        background: `${meta.accent}15`,
                        border: `1px solid ${meta.accent}30`,
                      }}
                    >
                      <Icon size={15} color={meta.accent} weight="duotone" />
                    </div>
                  )}
                  <div className="min-w-0 flex items-center gap-2">
                    <div className="min-w-0">
                      {renamingId === item.id ? (
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submitRename(item);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          onBlur={() => submitRename(item)}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={250}
                          data-testid={`asset-rename-input-${item.id}`}
                          className="text-[13.5px] text-white font-medium bg-[#0e0e16] border border-[#5e50f7]/45 rounded-md px-2 py-0.5 outline-none focus:border-[#5e50f7] min-w-[420px] max-w-[640px] w-full"
                        />
                      ) : (
                        <div
                          className="text-[13.5px] text-white truncate font-medium max-w-full"
                          title={item.name}
                          data-testid={`asset-title-${item.id}`}
                          style={{ maxWidth: "min(680px, calc(100vw - 620px))" }}
                        >
                          {item.name}
                        </div>
                      )}
                      <div className="text-[11px] text-[#a1a1a1]">
                        {meta.secondary || meta.label}
                      </div>
                    </div>
                    {renamingId !== item.id && (
                      <button
                        type="button"
                        data-testid={`asset-rename-btn-${item.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          startRename(item);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-white/[0.06] text-[#a1a1a1] hover:text-white"
                        aria-label="Átnevezés"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md font-medium"
                    style={{
                      background: `${meta.accent}15`,
                      color: meta.accent,
                      border: `1px solid ${meta.accent}30`,
                    }}
                  >
                    {meta.label}
                  </span>
                </div>
                <div className="text-[12px] text-[#c3c3cc]">
                  {item.saved_at ? new Date(item.saved_at).toLocaleDateString("hu-HU") : "-"}
                </div>
                <div
                  className="relative flex justify-end"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(menuOpen === item.id ? null : item.id);
                  }}
                >
                  <button
                    data-testid={`asset-menu-${item.id}`}
                    className="w-8 h-8 rounded-md hover:bg-white/[0.06] flex items-center justify-center text-[#a1a1a1] group-hover:text-white transition"
                  >
                    <DotsThreeVertical size={16} weight="bold" />
                  </button>
                  {menuOpen === item.id && (
                    <div
                      className="absolute right-0 top-9 z-20 min-w-[200px] rounded-xl bg-[#1f1f2b] border border-white/[0.1] shadow-2xl overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          handlePreview(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.05]"
                      >
                        <ArrowSquareOut size={14} /> Gyors előnézet
                      </button>
                      <button
                        onClick={() => {
                          handleOpen(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.05]"
                      >
                        <ArrowSquareOut size={14} /> Megnyitás a varázslóban
                      </button>
                      <button
                        onClick={() => {
                          startRename(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.05]"
                      >
                        <PencilSimple size={14} weight="bold" /> Átnevezés
                      </button>
                      <button
                        data-testid={`asset-md-${item.id}`}
                        onClick={() => {
                          handleDownloadMd(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.05]"
                      >
                        <FileText size={14} weight="bold" /> Letöltés Markdown
                      </button>
                      <button
                        data-testid={`asset-pdf-${item.id}`}
                        onClick={() => {
                          handleDownloadPdf(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] text-white hover:bg-white/[0.05]"
                      >
                        <FilePdf size={14} weight="bold" /> Letöltés PDF
                      </button>
                      <button
                        data-testid={`asset-delete-${item.id}`}
                        onClick={() => {
                          setConfirmDelete(item);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-[13px] hover:bg-white/[0.05]"
                        style={{ color: "#e57373" }}
                      >
                        <Trash size={14} weight="bold" /> Törlés
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!loading && items.length > 0 && (
        <div className="text-[11px] text-[#a1a1a1] mt-4">
          {filtered.length} / {items.length} anyag
        </div>
      )}

      <BlueprintPreviewModal
        open={!!previewItem && previewItem.agent_type !== "post-facebook" && previewItem.agent_type !== "post-linkedin"}
        agentType={previewItem?.agent_type}
        item={previewItem}
        onClose={() => setPreviewItem(null)}
      />

      <MagnetPostPreviewModal
        open={!!previewItem && (previewItem.agent_type === "post-facebook" || previewItem.agent_type === "post-linkedin")}
        item={previewItem}
        onClose={() => setPreviewItem(null)}
      />

      {/* Confirm-delete dialog */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={() => !deleting && setConfirmDelete(null)}
        >
          <div
            data-testid="asset-delete-modal"
            className="rounded-xl"
            style={{
              background: "#181822",
              border: "1px solid rgba(255,255,255,0.08)",
              padding: "24px 28px",
              maxWidth: 420,
              width: "92%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[16px] text-white font-semibold mb-2">
              Biztosan törlöd?
            </div>
            <div className="text-[13px] text-[#c3c3cc] leading-relaxed mb-5">
              A "{confirmDelete.name}" végleg eltűnik a Tudástárból. Ez a művelet
              nem vonható vissza.
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-[13px]"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#c0c0d0",
                }}
              >
                Mégsem
              </button>
              <button
                data-testid="asset-delete-confirm"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    const deletedId = confirmDelete.id;
                    const deletedType = confirmDelete.agent_type;
                    await api.delete(`/asset-vault/${deletedId}`);
                    setItems((prev) => prev.filter((x) => x.id !== deletedId));
                    // ── Aktív alap + agent draft sync ──────────────
                    // Ha a most törölt snapshot a `mex_active_alap`
                    // valamelyik rétegében aktívként szerepelt, töröljük
                    // azt — különben a következő agent-hívás 409-be
                    // futna. Plusz minden agent-draft cache lokálisan
                    // (csak a Foundation rétegek) tisztításra kerül, ha
                    // az ID-t cache-elte.
                    try {
                      const raw = localStorage.getItem("mex_active_alap");
                      if (raw) {
                        const obj = JSON.parse(raw) || {};
                        let changed = false;
                        if (deletedType && obj[deletedType] === deletedId) {
                          obj[deletedType] = null;
                          changed = true;
                        }
                        if (changed) {
                          localStorage.setItem("mex_active_alap", JSON.stringify(obj));
                        }
                      }
                      // Agent draft cache (a workflow lokálisan tárolja
                      // a betöltött snapshotot). A draft kulcsok
                      // formátuma `mex.draft.<agentKey>` — ezek nem
                      // ID-referencák, hanem teljes objektum cache-ek,
                      // amelyek a most törölt Foundation tartalmát
                      // tartalmazhatják. Biztonsági kétlépés: a
                      // megfelelő agent draft-ot is invalidáljuk.
                      if (deletedType === "voice")  localStorage.removeItem("mex.draft.voice");
                      if (deletedType === "client") localStorage.removeItem("mex.draft.client");
                      if (deletedType === "offer")  localStorage.removeItem("mex.draft.offer");
                    } catch { /* swallow best-effort cleanup */ }
                    setConfirmDelete(null);
                  } catch (err) {
                    // keep dialog open on error
                  } finally {
                    setDeleting(false);
                  }
                }}
                className="px-4 py-2 rounded-lg text-[13px] font-medium"
                style={{
                  background: "rgba(229,115,115,0.15)",
                  border: "1px solid rgba(229,115,115,0.40)",
                  color: "#ff9b9b",
                }}
              >
                {deleting ? "Törlés…" : "Törlés"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ search, total }) {
  return (
    <div className="p-14 text-center">
      <div className="inline-flex w-12 h-12 rounded-xl bg-[#1f1f2b] border border-white/[0.08] items-center justify-center mb-4">
        <Vault size={22} color="#5e50f7" weight="duotone" />
      </div>
      <div className="text-[14px] text-white font-medium mb-1">
        {total === 0 ? "Még nincs mentett anyag" : "Nincs találat"}
      </div>
      <div className="text-[12px] text-[#a1a1a1] max-w-sm mx-auto">
        {total === 0
          ? "Nyiss meg egy Alapozás varázslót, generáld le az anyagot, majd kattints a Tudástárba mentésre."
          : `A "${search}" keresésre nincs találat az aktuális szűrővel.`}
      </div>
    </div>
  );
}
